import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Heart, Activity, Wind, Gauge, Brain, Zap, ShieldCheck,
  Camera, CameraOff, ChevronRight, RotateCcw, Loader2,
  AlertTriangle, CheckCircle, ArrowLeft, Droplets, Timer,
  TrendingUp, Salad, FlaskConical, Dumbbell, X
} from 'lucide-react';
import { toast } from 'sonner';
import RPPGEngine from '../lib/rppgEngine';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VITAL_META = {
  heart_rate: { label: 'Heart Rate', unit: 'BPM', icon: Heart, color: 'rose', ranges: [60, 100] },
  bp_systolic: { label: 'BP Systolic', unit: 'mmHg', icon: Activity, color: 'red', ranges: [90, 140] },
  bp_diastolic: { label: 'BP Diastolic', unit: 'mmHg', icon: Activity, color: 'red', ranges: [60, 90] },
  respiratory_rate: { label: 'Breathing', unit: '/min', icon: Wind, color: 'sky', ranges: [12, 20] },
  spo2: { label: 'SpO2', unit: '%', icon: Droplets, color: 'blue', ranges: [95, 100] },
  stress_index: { label: 'Stress', unit: '/100', icon: Brain, color: 'amber', ranges: [0, 50] },
  hrv: { label: 'HRV (SDNN)', unit: 'ms', icon: Zap, color: 'violet', ranges: [20, 80] },
  cardiac_output: { label: 'Cardiac Output', unit: 'L/min', icon: TrendingUp, color: 'emerald', ranges: [4, 8] },
};

function getStatus(key, val) {
  const m = VITAL_META[key];
  if (!m || val == null) return 'unknown';
  if (key === 'stress_index') return val <= 35 ? 'normal' : val <= 60 ? 'attention' : 'high';
  if (val >= m.ranges[0] && val <= m.ranges[1]) return 'normal';
  return Math.abs(val - m.ranges[0]) > Math.abs(val - m.ranges[1]) ? 'high' : 'low';
}

const STATUS_STYLES = {
  normal: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  attention: 'bg-amber-50 border-amber-200 text-amber-700',
  high: 'bg-rose-50 border-rose-200 text-rose-700',
  low: 'bg-sky-50 border-sky-200 text-sky-700',
  unknown: 'bg-slate-50 border-slate-200 text-slate-500',
};

export default function FaceVitalsScanner({ clinicCode, doctorId, patientPhone, patientName, onClose, onComplete, embedded }) {
  const [phase, setPhase] = useState('consent');
  const [cameraReady, setCameraReady] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [faceOk, setFaceOk] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [liveVitals, setLiveVitals] = useState(null);
  const [vitals, setVitals] = useState(null);
  const [scanId, setScanId] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiDialog, setShowAiDialog] = useState(false);
  const [faceScreening, setFaceScreening] = useState(null);
  const [faceScreeningLoading, setFaceScreeningLoading] = useState(false);
  const [showScreeningDialog, setShowScreeningDialog] = useState(false);
  const [userInfo, setUserInfo] = useState({ age: '', gender: '', height_cm: '', weight_kg: '' });

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const streamRef = useRef(null);

  const stopCamera = useCallback(() => {
    if (engineRef.current) { engineRef.current.stop(); engineRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
  }, []);

  useEffect(() => { return () => stopCamera(); }, [stopCamera]);

  // Attach stream to video element when calibrate/scanning phase renders
  useEffect(() => {
    if (streamRef.current && videoRef.current && (phase === 'calibrate' || phase === 'scanning')) {
      if (!videoRef.current.srcObject) {
        videoRef.current.srcObject = streamRef.current;
        videoRef.current.play().catch(() => {});
      }
    }
  }, [phase]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      streamRef.current = stream;
      setCameraReady(true);
      setPhase('calibrate');
      // Stream will be attached via the useEffect above once video element mounts
    } catch (err) {
      toast.error('Camera access denied. Please allow camera access to scan vitals.');
    }
  };

  const captureFrame = () => {
    try {
      const video = videoRef.current;
      if (!video || !video.videoWidth) return null;
      const c = document.createElement('canvas');
      c.width = video.videoWidth;
      c.height = video.videoHeight;
      c.getContext('2d').drawImage(video, 0, 0);
      return c.toDataURL('image/jpeg', 0.8).split(',')[1];
    } catch { return null; }
  };

  const startScan = () => {
    if (!videoRef.current || !canvasRef.current) return;
    setScanning(true);
    setPhase('scanning');
    setProgress(0);
    setFaceOk(false);

    const engine = new RPPGEngine(
      videoRef.current, canvasRef.current,
      (prog, face, sec, live) => { setProgress(prog); setFaceOk(face); setElapsed(Math.floor(sec)); if (live) setLiveVitals(live); },
      (result) => {
        const frameB64 = captureFrame();
        setScanning(false);
        if (result.error) { toast.error(result.error); setPhase('calibrate'); return; }
        setVitals(result);
        setPhase('results');
        saveScan(result, frameB64);
      }
    );
    engineRef.current = engine;
    engine.start();
  };

  const saveScan = async (v, frameB64) => {
    try {
      const body = {
        vitals: v,
        patient_phone: patientPhone || '',
        patient_name: patientName || '',
        doctor_id: doctorId || '',
        clinic_code: clinicCode || '',
        source: clinicCode ? 'kiosk_checkin' : 'self_scan',
        age: parseInt(userInfo.age) || null,
        gender: userInfo.gender || null,
        height_cm: parseFloat(userInfo.height_cm) || null,
        weight_kg: parseFloat(userInfo.weight_kg) || null,
        face_image: frameB64 || null,
      };
      const res = await fetch(`${API_URL}/api/face-vitals/save-scan`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
      if (res.ok) {
        const data = await res.json();
        setScanId(data.id);
        pollAI(data.id, !!frameB64);
      }
    } catch (e) { console.error('Save scan failed:', e); }
  };

  const pollAI = async (id, hasImage) => {
    setAiLoading(true);
    if (hasImage) setFaceScreeningLoading(true);
    let aiDone = false, screenDone = !hasImage;
    for (let i = 0; i < 20; i++) {
      if (aiDone && screenDone) break;
      await new Promise(r => setTimeout(r, 2000));
      try {
        const res = await fetch(`${API_URL}/api/face-vitals/scan/${id}`);
        if (res.ok) {
          const data = await res.json();
          if (!aiDone && data.ai_analysis) {
            if (!data.ai_analysis.error) setAiAnalysis(data.ai_analysis);
            setAiLoading(false); aiDone = true;
          }
          if (!screenDone && data.face_screening) {
            if (!data.face_screening.error) setFaceScreening(data.face_screening);
            setFaceScreeningLoading(false); screenDone = true;
          }
        }
      } catch {}
    }
    setAiLoading(false);
    setFaceScreeningLoading(false);
  };

  const retake = () => {
    setVitals(null); setScanId(null); setAiAnalysis(null); setFaceScreening(null);
    setPhase('calibrate'); setProgress(0);
  };

  // -- CONSENT PHASE --
  if (phase === 'consent') {
    return (
      <div className={`${embedded ? '' : 'min-h-screen bg-gradient-to-br from-slate-50 to-teal-50/30'} flex items-center justify-center p-4`}>
        <Card className="max-w-md w-full border-slate-200 shadow-xl" data-testid="face-vitals-consent">
          <CardContent className="p-6 space-y-5">
            {onClose && (
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600 absolute top-3 right-3"><X className="w-5 h-5" /></button>
            )}
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center mx-auto mb-3">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Face Vitals Scan</h2>
              <p className="text-sm text-slate-500 mt-1">30-second contactless wellness screening</p>
            </div>

            <div className="bg-teal-50 border border-teal-100 rounded-xl p-4 space-y-2 text-sm text-teal-800">
              <p className="font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Privacy First</p>
              <ul className="space-y-1 text-xs text-teal-700 pl-6 list-disc">
                <li>Camera feed is processed <b>on your device</b></li>
                <li>A single frame is sent for AI face screening analysis</li>
                <li>Images are not stored — only AI results are saved</li>
              </ul>
            </div>

            <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-700 flex gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <p><b>Disclaimer:</b> This is a wellness screening tool, not a medical device. Results are estimates and should not replace professional medical advice.</p>
            </div>

            <div className="space-y-3">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Optional Details (improves accuracy)</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Age</Label>
                  <Input data-testid="fv-age" type="number" value={userInfo.age} onChange={e => setUserInfo(p => ({ ...p, age: e.target.value }))} placeholder="e.g. 30" className="h-9 mt-0.5" />
                </div>
                <div>
                  <Label className="text-xs">Gender</Label>
                  <select data-testid="fv-gender" value={userInfo.gender} onChange={e => setUserInfo(p => ({ ...p, gender: e.target.value }))} className="w-full h-9 mt-0.5 rounded-md border border-input bg-background px-3 text-sm">
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Height (cm)</Label>
                  <Input data-testid="fv-height" type="number" value={userInfo.height_cm} onChange={e => setUserInfo(p => ({ ...p, height_cm: e.target.value }))} placeholder="170" className="h-9 mt-0.5" />
                </div>
                <div>
                  <Label className="text-xs">Weight (kg)</Label>
                  <Input data-testid="fv-weight" type="number" value={userInfo.weight_kg} onChange={e => setUserInfo(p => ({ ...p, weight_kg: e.target.value }))} placeholder="70" className="h-9 mt-0.5" />
                </div>
              </div>
            </div>

            <Button data-testid="fv-start-camera-btn" onClick={startCamera} className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white text-base">
              <Camera className="w-5 h-5 mr-2" /> Allow Camera & Start
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // -- CALIBRATE PHASE --
  if (phase === 'calibrate') {
    return (
      <div className={`${embedded ? '' : 'min-h-screen bg-slate-950'} flex flex-col items-center justify-center p-4`}>
        <div className="max-w-md w-full space-y-4">
          <div className="relative rounded-2xl overflow-hidden bg-black aspect-[4/3]">
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover mirror" style={{ transform: 'scaleX(-1)' }} />
            <canvas ref={canvasRef} className="hidden" />
            {/* Face alignment overlay */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-48 h-60 border-2 border-dashed border-teal-400/70 rounded-[50%] relative">
                <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-black/60 text-teal-300 text-xs px-3 py-1 rounded-full whitespace-nowrap">
                  Align your face here
                </div>
              </div>
            </div>
            {onClose && (
              <button onClick={() => { stopCamera(); onClose(); }} className="absolute top-3 right-3 bg-black/50 text-white rounded-full p-1.5 hover:bg-black/70">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="text-center space-y-2">
            <p className="text-slate-300 text-sm">Position your face within the oval. Ensure good lighting.</p>
            <ul className="text-xs text-slate-400 space-y-1">
              <li>Keep still during the scan</li>
              <li>Remove glasses if possible</li>
              <li>Face the camera directly</li>
            </ul>
          </div>

          <Button data-testid="fv-begin-scan-btn" onClick={startScan} className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white text-base">
            <Heart className="w-5 h-5 mr-2 animate-pulse" /> Begin 30s Scan
          </Button>

          <Button variant="ghost" onClick={() => { stopCamera(); setPhase('consent'); setCameraReady(false); }} className="w-full text-slate-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </div>
    );
  }

  // -- SCANNING PHASE --
  if (phase === 'scanning') {
    const pct = Math.round(progress * 100);
    const remaining = Math.max(0, 30 - elapsed);
    const radius = 62;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference * (1 - progress);

    return (
      <div className={`${embedded ? '' : 'min-h-screen bg-slate-950'} flex flex-col items-center justify-center p-4`}>
        <div className="max-w-md w-full space-y-4">
          <div className="relative rounded-2xl overflow-hidden bg-black aspect-[4/3]">
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{ transform: 'scaleX(-1)' }} />
            <canvas ref={canvasRef} className="hidden" />

            {/* Large progress ring overlay */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <svg width="160" height="160" viewBox="0 0 160 160" className="drop-shadow-lg" style={{ filter: 'drop-shadow(0 0 12px rgba(20,184,166,0.4))' }}>
                <circle cx="80" cy="80" r={radius} fill="rgba(0,0,0,0.35)" stroke="rgba(255,255,255,0.2)" strokeWidth="6" />
                <circle cx="80" cy="80" r={radius} fill="none"
                  stroke={faceOk ? '#14b8a6' : '#f59e0b'}
                  strokeWidth="6" strokeLinecap="round"
                  strokeDasharray={circumference} strokeDashoffset={dashOffset}
                  style={{ transition: 'stroke-dashoffset 0.5s ease', transform: 'rotate(-90deg)', transformOrigin: 'center' }}
                />
                <text x="80" y="70" textAnchor="middle" fill="white" fontSize="28" fontWeight="bold" fontFamily="Manrope">{remaining}s</text>
                <text x="80" y="92" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize="14" fontWeight="600">{pct}%</text>
              </svg>
            </div>

            {/* Face detection indicator */}
            <div className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${faceOk ? 'bg-emerald-500/80 text-white' : 'bg-amber-500/80 text-white'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${faceOk ? 'bg-white animate-pulse' : 'bg-yellow-200'}`} />
              {faceOk ? 'Face Detected' : 'Align Face'}
            </div>

            {onClose && (
              <button onClick={() => { stopCamera(); onClose(); }} className="absolute top-3 right-3 bg-black/50 text-white rounded-full p-1.5 hover:bg-black/70">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="text-center">
            <p className="text-teal-400 text-sm font-medium animate-pulse">Scanning vitals...</p>
            <p className="text-slate-500 text-xs mt-1">Keep still and breathe normally</p>
          </div>

          {/* Real-time vitals preview */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-slate-800/80 border border-slate-700 rounded-xl px-4 py-3 flex items-center gap-2">
              <Heart className={`w-5 h-5 ${liveVitals?.heart_rate ? 'text-rose-400 animate-pulse' : 'text-slate-500'}`} />
              <div>
                <span className="text-white font-bold text-lg" style={{ fontFamily: 'Manrope' }}>
                  {liveVitals?.heart_rate || '--'}
                </span>
                <span className="text-slate-400 text-xs ml-1">BPM</span>
              </div>
            </div>
            <div className="bg-slate-800/80 border border-slate-700 rounded-xl px-4 py-3 flex items-center gap-2">
              <Droplets className={`w-5 h-5 ${liveVitals?.spo2 ? 'text-blue-400' : 'text-slate-500'}`} />
              <div>
                <span className="text-white font-bold text-lg" style={{ fontFamily: 'Manrope' }}>
                  {liveVitals?.spo2 || '--'}
                </span>
                <span className="text-slate-400 text-xs ml-1">SpO2%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // -- RESULTS PHASE --
  if (phase === 'results' && vitals) {
    const vitalKeys = ['heart_rate', 'bp_systolic', 'bp_diastolic', 'spo2', 'respiratory_rate', 'stress_index', 'hrv', 'cardiac_output'];
    const hasAttention = vitalKeys.some(k => {
      const s = getStatus(k, vitals[k]);
      return s === 'high' || s === 'low' || s === 'attention';
    });

    return (
      <div className={`${embedded ? '' : 'min-h-screen bg-gradient-to-br from-slate-50 to-teal-50/30'} p-4`}>
        <div className="max-w-lg mx-auto space-y-4 pb-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Scan Results</h2>
              <p className="text-xs text-slate-500">Signal Quality: {vitals.signal_quality}%</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={retake} data-testid="fv-retake-btn">
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Retake
              </Button>
              {onClose && (
                <Button variant="ghost" size="sm" onClick={() => { stopCamera(); onClose(); }}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>

          {/* Overall status */}
          <div className={`rounded-xl p-4 border ${hasAttention ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
            <div className="flex items-center gap-3">
              {hasAttention ? <AlertTriangle className="w-6 h-6 text-amber-600" /> : <CheckCircle className="w-6 h-6 text-emerald-600" />}
              <div>
                <p className={`font-bold ${hasAttention ? 'text-amber-800' : 'text-emerald-800'}`} style={{ fontFamily: 'Manrope' }}>
                  {hasAttention ? 'Some Vitals Need Attention' : 'All Vitals Normal'}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">Estimated results - consult a doctor for clinical accuracy</p>
              </div>
            </div>
          </div>

          {/* Vital cards grid */}
          <div className="grid grid-cols-2 gap-3" data-testid="fv-vitals-grid">
            {vitalKeys.map(key => {
              const meta = VITAL_META[key];
              const val = vitals[key];
              const status = getStatus(key, val);
              const Icon = meta.icon;
              return (
                <div key={key} className={`rounded-xl p-3 border ${STATUS_STYLES[status]}`} data-testid={`fv-vital-${key}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4 opacity-70" />
                    <span className="text-xs font-medium opacity-80">{meta.label}</span>
                  </div>
                  <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                    {key === 'bp_systolic' ? `${vitals.bp_systolic}/${vitals.bp_diastolic}` : val}
                    <span className="text-xs font-normal ml-1 opacity-60">{key === 'bp_systolic' ? 'mmHg' : meta.unit}</span>
                  </p>
                  <Badge variant="outline" className={`text-[10px] mt-1 ${status === 'normal' ? 'border-emerald-300 text-emerald-600' : status === 'high' || status === 'attention' ? 'border-rose-300 text-rose-600' : 'border-sky-300 text-sky-600'}`}>
                    {status === 'normal' ? 'Normal' : status === 'high' || status === 'attention' ? 'Needs Attention' : 'Low'}
                  </Badge>
                </div>
              );
            })}
          </div>

          {/* Extra vitals */}
          <div className="bg-white rounded-xl p-4 border border-slate-200 space-y-2">
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Additional</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Pulse</span><span className="font-medium">{vitals.pulse_regularity}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Vascular Age</span><span className="font-medium">{vitals.vascular_age} yrs</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Samples</span><span className="font-medium">{vitals.samples_collected}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">FPS</span><span className="font-medium">{vitals.actual_fps}</span></div>
            </div>
          </div>

          {/* AI Recommendations button */}
          <Button
            data-testid="fv-ai-insights-btn"
            onClick={() => setShowAiDialog(true)}
            className="w-full h-12 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white"
            disabled={aiLoading && !aiAnalysis}
          >
            {aiLoading && !aiAnalysis ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> AI Analysis in Progress...</>
            ) : (
              <><Brain className="w-5 h-5 mr-2" /> View AI Health Insights</>
            )}
          </Button>

          {/* Face Screening button */}
          <Button
            data-testid="fv-face-screening-btn"
            onClick={() => setShowScreeningDialog(true)}
            className="w-full h-12 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white"
            disabled={faceScreeningLoading && !faceScreening}
          >
            {faceScreeningLoading && !faceScreening ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> 1Clin Face Screening in Progress...</>
            ) : (
              <><Camera className="w-5 h-5 mr-2" /> View 1Clin AI Face Screening</>
            )}
          </Button>

          {/* Complete/Continue for kiosk mode */}
          {onComplete && (
            <Button
              data-testid="fv-continue-btn"
              variant="outline"
              onClick={() => onComplete(vitals, scanId)}
              className="w-full h-11 border-teal-300 text-teal-700 hover:bg-teal-50"
            >
              Continue to Check-In <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}

          {/* AI Dialog */}
          <Dialog open={showAiDialog} onOpenChange={setShowAiDialog}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Brain className="w-5 h-5 text-teal-600" /> AI Health Insights
                </DialogTitle>
              </DialogHeader>

              {aiLoading && !aiAnalysis ? (
                <div className="text-center py-10">
                  <Loader2 className="w-8 h-8 animate-spin text-teal-600 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">Analyzing your vitals with AI...</p>
                </div>
              ) : aiAnalysis ? (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className={`rounded-xl p-4 border ${aiAnalysis.overall_status === 'Normal' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                    <p className="font-bold text-sm" style={{ fontFamily: 'Manrope' }}>{aiAnalysis.overall_status}</p>
                    <p className="text-sm text-slate-600 mt-1">{aiAnalysis.summary}</p>
                  </div>

                  {/* Recommended Tests */}
                  {aiAnalysis.recommended_tests?.length > 0 && (
                    <div>
                      <p className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2"><FlaskConical className="w-4 h-4 text-violet-500" /> Recommended Tests</p>
                      <div className="space-y-2">
                        {aiAnalysis.recommended_tests.map((t, i) => (
                          <div key={`el-${i}`} className="bg-violet-50 border border-violet-100 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-violet-800">{t.test_name}</p>
                              <Badge variant="outline" className={`text-[10px] ${t.urgency === 'urgent' ? 'border-rose-300 text-rose-600' : t.urgency === 'soon' ? 'border-amber-300 text-amber-600' : 'border-slate-300 text-slate-500'}`}>{t.urgency}</Badge>
                            </div>
                            <p className="text-xs text-violet-600 mt-0.5">{t.reason}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Diet Plan */}
                  {aiAnalysis.diet_plan && (
                    <div>
                      <p className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2"><Salad className="w-4 h-4 text-emerald-500" /> Diet Plan</p>
                      <div className="space-y-2 text-sm">
                        {aiAnalysis.diet_plan.foods_to_include?.length > 0 && (
                          <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                            <p className="font-medium text-emerald-700 text-xs mb-1">Include</p>
                            <ul className="text-xs text-emerald-600 space-y-0.5 list-disc pl-4">
                              {aiAnalysis.diet_plan.foods_to_include.map((f, i) => <li key={`${f}-${i}`}>{f}</li>)}
                            </ul>
                          </div>
                        )}
                        {aiAnalysis.diet_plan.foods_to_avoid?.length > 0 && (
                          <div className="bg-rose-50 border border-rose-100 rounded-lg p-3">
                            <p className="font-medium text-rose-700 text-xs mb-1">Avoid</p>
                            <ul className="text-xs text-rose-600 space-y-0.5 list-disc pl-4">
                              {aiAnalysis.diet_plan.foods_to_avoid.map((f, i) => <li key={`${f}-${i}`}>{f}</li>)}
                            </ul>
                          </div>
                        )}
                        {aiAnalysis.diet_plan.meal_suggestions?.length > 0 && (
                          <div className="bg-sky-50 border border-sky-100 rounded-lg p-3">
                            <p className="font-medium text-sky-700 text-xs mb-1">Meal Ideas</p>
                            <ul className="text-xs text-sky-600 space-y-0.5 list-disc pl-4">
                              {aiAnalysis.diet_plan.meal_suggestions.map((f, i) => <li key={`${f}-${i}`}>{f}</li>)}
                            </ul>
                          </div>
                        )}
                        {aiAnalysis.diet_plan.hydration && (
                          <p className="text-xs text-blue-600 bg-blue-50 border border-blue-100 rounded-lg p-2"><Droplets className="w-3 h-3 inline mr-1" />{aiAnalysis.diet_plan.hydration}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Lifestyle */}
                  {aiAnalysis.lifestyle_recommendations?.length > 0 && (
                    <div>
                      <p className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2"><Dumbbell className="w-4 h-4 text-amber-500" /> Lifestyle Tips</p>
                      <ul className="text-xs text-slate-600 space-y-1 list-disc pl-5">
                        {aiAnalysis.lifestyle_recommendations.map((l, i) => <li key={`${l}-${i}`}>{l}</li>)}
                      </ul>
                    </div>
                  )}

                  {aiAnalysis.follow_up && (
                    <div className="bg-teal-50 border border-teal-100 rounded-lg p-3">
                      <p className="text-xs text-teal-700"><Timer className="w-3 h-3 inline mr-1" /><b>Follow-up:</b> {aiAnalysis.follow_up}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-10 text-slate-400">
                  <Brain className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">AI analysis is not available yet. Please try again later.</p>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Face Screening Dialog */}
          <Dialog open={showScreeningDialog} onOpenChange={setShowScreeningDialog}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Camera className="w-5 h-5 text-indigo-600" /> 1Clin AI Face Screening
                </DialogTitle>
              </DialogHeader>

              {faceScreeningLoading && !faceScreening ? (
                <div className="text-center py-10">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">Analyzing your face for health indicators...</p>
                </div>
              ) : faceScreening ? (
                <FaceScreeningContent screening={faceScreening} />
              ) : (
                <div className="text-center py-10 text-slate-400">
                  <Camera className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Face screening is not available yet. Please try again later.</p>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
    );
  }

  return null;
}

function FaceScreeningContent({ screening }) {
  const getSeverityColor = (severity) => {
    if (!severity) return 'slate';
    const s = severity.toLowerCase();
    if (s === 'none' || s === 'well-hydrated' || s === 'excellent') return 'emerald';
    if (s === 'mild' || s === 'mildly-dehydrated' || s === 'good') return 'amber';
    if (s === 'moderate' || s === 'dehydrated' || s === 'fair') return 'orange';
    if (s === 'severe' || s === 'poor') return 'rose';
    return 'slate';
  };

  const colorClasses = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-600',
  };

  return (
    <div className="space-y-4" data-testid="face-screening-content">
      {/* Score & Confidence */}
      <div className="flex gap-3">
        <div className="flex-1 rounded-xl p-4 border bg-indigo-50 border-indigo-200 text-center">
          <p className="text-3xl font-bold text-indigo-700" style={{ fontFamily: 'Manrope' }}>{screening.overall_skin_health_score ?? '--'}</p>
          <p className="text-xs text-indigo-500 mt-1">Skin Health Score</p>
        </div>
        <div className="flex-1 rounded-xl p-4 border bg-violet-50 border-violet-200 text-center">
          <p className="text-3xl font-bold text-violet-700" style={{ fontFamily: 'Manrope' }}>{screening.ai_confidence ?? '--'}%</p>
          <p className="text-xs text-violet-500 mt-1">AI Confidence</p>
        </div>
      </div>

      {/* Summary */}
      <div className="rounded-xl p-4 border bg-slate-50 border-slate-200">
        <p className="text-sm text-slate-700">{screening.summary}</p>
      </div>

      {/* Screenings */}
      {screening.screenings?.length > 0 && (
        <div>
          <p className="text-sm font-bold text-slate-700 mb-2">Screening Results</p>
          <div className="space-y-2">
            {screening.screenings.map((s, i) => {
              const c = getSeverityColor(s.severity);
              return (
                <div key={i} className={`rounded-lg p-3 border ${colorClasses[c]}`} data-testid={`screening-item-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-semibold">{s.name}</p>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] capitalize">{s.severity}</Badge>
                      <span className="text-xs font-bold opacity-70">{s.score}/100</span>
                    </div>
                  </div>
                  <p className="text-xs opacity-80">{s.detail}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {screening.recommendations?.length > 0 && (
        <div>
          <p className="text-sm font-bold text-slate-700 mb-2">Recommendations</p>
          <ul className="text-xs text-slate-600 space-y-1 list-disc pl-5">
            {screening.recommendations.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}

      {/* Disclaimer */}
      {screening.disclaimer && (
        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
          <p className="text-[11px] text-amber-700 flex gap-2"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{screening.disclaimer}</p>
        </div>
      )}
    </div>
  );
}
